import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Globe, Phone, Mail, ChevronDown } from "lucide-react";
import { API } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

const STATUS_META = {
  application_received: { label: "New application", cls: "bg-amber-100 text-amber-800" },
  under_review: { label: "Under review", cls: "bg-amber-100 text-amber-800" },
  approved: { label: "Approved", cls: "bg-moss-light text-moss" },
  active: { label: "Active", cls: "bg-moss-light text-moss" },
  suspended: { label: "Suspended", cls: "bg-terracotta/15 text-terracotta" },
  terminated: { label: "Terminated", cls: "bg-ink/10 text-ink-secondary" },
};
const ACTIONS = {
  application_received: [["under_review", "Start review"], ["active", "Approve & activate"], ["terminated", "Reject"]],
  under_review: [["active", "Approve & activate"], ["terminated", "Reject"]],
  approved: [["active", "Activate"], ["suspended", "Suspend"]],
  active: [["suspended", "Suspend"], ["terminated", "Terminate"]],
  suspended: [["active", "Reactivate"], ["terminated", "Terminate"]],
  terminated: [],
};

function PartnerRow({ p, onChange }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const meta = STATUS_META[p.status] || STATUS_META.application_received;

  const setStatus = async (status) => {
    setBusy(true); setOpen(false);
    try {
      const res = await fetch(`${API}/admin/mpn/partners/${p.id}/status`, {
        method: "PATCH", headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success("Partner updated"); onChange();
    } catch { toast.error("Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-sand-dark bg-white p-4" data-testid={`partner-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-bold text-ink">{p.name}</p>
            {p.partner_id && <code className="text-xs font-mono bg-sand px-2 py-0.5 rounded text-terracotta" data-testid={`partner-id-${p.id}`}>{p.partner_id}</code>}
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`} data-testid={`partner-status-${p.id}`}>{meta.label}</span>
          </div>
          {p.business_name && <p className="text-sm text-ink-secondary">{p.business_name}</p>}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-ink-muted">
            <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {p.email}</span>
            {p.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {p.phone}</span>}
            {p.country && <span className="inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> {p.country}</span>}
          </div>
          {p.channels && <p className="text-xs text-ink-muted mt-1">Channels: {p.channels}</p>}
        </div>
        <div className="relative shrink-0">
          <button onClick={() => setOpen((o) => !o)} onBlur={() => setTimeout(() => setOpen(false), 150)} disabled={busy || !ACTIONS[p.status]?.length}
            data-testid={`partner-actions-${p.id}`}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-sand-dark text-ink text-xs font-semibold hover:border-moss transition-colors disabled:opacity-40">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <>Manage <ChevronDown className="w-3.5 h-3.5" /></>}
          </button>
          {open && (
            <div className="absolute right-0 mt-1 w-44 bg-white border border-sand-dark rounded-sm shadow-lg z-10 overflow-hidden">
              {ACTIONS[p.status].map(([status, label]) => (
                <button key={status} onMouseDown={() => setStatus(status)} data-testid={`partner-set-${status}-${p.id}`}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-sand text-ink">{label}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MPNPartnersAdmin() {
  const [partners, setPartners] = useState(null);
  const load = () => fetch(`${API}/admin/mpn/partners`, { headers: authHeader() }).then((r) => r.json()).then(setPartners).catch(() => setPartners([]));
  useEffect(() => { load(); }, []);

  const counts = partners ? partners.reduce((a, p) => { a[p.status] = (a[p.status] || 0) + 1; return a; }, {}) : {};

  return (
    <div data-testid="mpn-partners-admin" className="space-y-4">
      <div className="flex items-center gap-2">
        <Megaphone className="w-5 h-5 text-terracotta" />
        <h2 className="font-display font-extrabold text-lg">Marketing Partners</h2>
        {partners && <span className="text-sm text-ink-muted">· {partners.length} total{counts.application_received ? ` · ${counts.application_received} new` : ""}</span>}
      </div>
      {partners == null ? (
        <div className="text-ink-secondary flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading partners…</div>
      ) : partners.length === 0 ? (
        <p className="text-ink-secondary text-sm">No partner applications yet.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {partners.map((p) => <PartnerRow key={p.id} p={p} onChange={load} />)}
        </div>
      )}
    </div>
  );
}
